import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { apiErrorText, apiRequest } from '../../utils';
import '../../pages/event/MemberHome.css';
import '../../pages/event/CompanyFeed.css';
import './CommunityWall.css';
/* For `.gw-open` alone — the one-line door to the guess-who. The board itself lives on its own
   page now, but the door is still styled with the board's stylesheet, so this wall must say it
   needs it rather than inherit it from whoever happens to have imported it first. */
import './GuessWho.css';

/* The company's people, with their faces on.

   ★ ONE component on two screens — the Community tab of /engagement and the People chip of the
   photo wall — off ONE endpoint, for the same reason `EventSocial` is one component on the
   public event page and inside the host screen: two renderings of one set of people drift, and
   the one nobody checks is the one that goes wrong.

   ★ There is no number anywhere on it. What a tile says is what that person DOES — comes along,
   posts, answers, brings people — never what they are worth. A personal score people can see is
   a culture decision rather than a feature toggle, so the wall says the facts and leaves the
   arithmetic on the admin's own tab.

   ★ The filters are what people SAID about themselves. Behaviour first, because it answers "who
   is actually here", and then everything they wrote — hobbies, languages, what to ask them about,
   where they sit, what they do, coffee or tea. The company's TAGS are the one thing that is not
   offered to a colleague: those are the office's word for its people, a way of getting a message
   to the right ones, and a community screen is for being curious about a person rather than for
   reading their label. They stay, admin-only, at the bottom beside the two diagnostics.

   ★ The four TAG rows are on the wall itself, not behind "more filters". They are the vocabulary
   this community grew for itself — every word on them was typed by somebody here — and a drawer
   made the only labels anybody could see the company's own, which reads as if the office does the
   describing and the people do not. What stays in the drawer is the half that is a fact rather
   than a word people chose: where somebody sits, whose team they are on, coffee or tea, how long
   they have been here.

   ★ Everything is filtered here, in the browser. The whole roster arrives in one payload, so
   the search never round-trips and the chip counts are free. They count the WHOLE wall and not
   the filtered view: a number that shrinks as you click is not a guide to what clicking would
   do. */

/* Within a row, picking two chips means EITHER — a chip row is a shortlist. Across rows it means
   BOTH, because the rows ask different questions. Anything else and four clicks reliably find
   nobody. */
const matchesAny = (picked, values) =>
  picked.length === 0 || picked.some((one) => (values || []).includes(one));

/* The comparison form of anything people typed. `based_in` and `department` are free text, so
   'Dubai Marina' and 'dubai marina' are one chip and one filter; the chip shows the first
   spelling seen, which is the one somebody actually wrote. */
const norm = (value) => String(value || '').trim().toLowerCase();

/* How you reach somebody, in the order people actually do it. At most TWO make it onto a tile —
   see the note at the render site; the profile behind the face carries every one of them. */
const REACH = [
  { key: 'linkedin', icon: 'linkedin', title: 'LinkedIn',
    href: (v) => (v.startsWith('http') ? v : `https://linkedin.com/in/${v}`) },
  { key: 'phone', icon: 'whatsapp', title: 'WhatsApp',
    href: (v) => `https://wa.me/${v.replace(/\D/g, '').replace(/^00/, '')}` },
  { key: 'instagram', icon: 'instagram', title: 'Instagram',
    href: (v) => (v.startsWith('http') ? v : `https://instagram.com/${v}`) },
  { key: 'facebook', icon: 'facebook', title: 'Facebook',
    href: (v) => (v.startsWith('http') ? v : `https://facebook.com/${v}`) },
];

const reachOf = (person) => REACH
  /* A phone number of only punctuation is not a WhatsApp account — build the link, then keep it
     only if something survived the strip. */
  .filter((one) => {
    const raw = String(person[one.key] || '').trim();
    return raw && (one.key !== 'phone' || raw.replace(/\D/g, ''));
  })
  .slice(0, 2)
  .map((one, i) => ({ ...one, offset: `${5 + i * 25}px`,
                      href: one.href(String(person[one.key]).trim()) }));

const DID_ICONS = {
  hosted: 'star',
  showing_up: 'check',
  saying: 'comment',
  missions: 'flag',
  answering: 'list-check',
  curious: 'bulb',
  bringing: 'user-add',
};

/* Which profile fields become a row of chips, in the order they are drawn, and how one person's
   answer is read as a list of values. A single free-text answer is a list of one — that is the
   whole difference between `based_in` and `hobbies` as far as filtering goes.

   ★ `role` is deliberately NOT here: "Head of marketing, EMEA" is nobody's chip. It is on the tile
   and it is in the search box, which is where a job title belongs. */
const CHIP_FIELDS = [
  { key: 'hobbies', label: 'pf_hobbies', tags: true, values: (p) => p.hobbies || [] },
  { key: 'languages', label: 'pf_languages', tags: true, values: (p) => p.languages || [] },
  { key: 'ask_me_about', label: 'pf_ask_me_about', tags: true, values: (p) => p.ask_me_about || [] },
  { key: 'based_in', label: 'pf_based_in', values: (p) => (p.based_in ? [p.based_in] : []) },
  { key: 'department', label: 'pf_department', values: (p) => (p.department ? [p.department] : []) },
  {
    key: 'this_or_that',
    label: 'pf_this_or_that',
    /* One person's four answers as tokens ('drink:coffee'), so the pairs can be chips without a
       row per pair. The label is translated on the way out, never stored. */
    values: (p) => Object.entries(p.this_or_that || {}).map(([k, v]) => `${k}:${v}`),
    translate: (value, t) => t(`pfTotv_${value.split(':')[1]}`),
  },
  {
    key: 'open_to',
    label: 'pf_open_to',
    tags: true,
    values: (p) => p.open_to || [],
    /* ★ The five the platform suggests carry a translated label; `open_to` became a free chip
       editor on 2026-09-08, so anything else is a word somebody typed and reads as they typed
       it. Without the default, i18next draws the missing KEY — 'pfOpen_reviewing a cv'. */
    translate: (value, t) => t(`pfOpen_${value}`, { defaultValue: value }),
  },
];

/* On the wall itself, and behind "more filters". A word somebody chose for themselves is a tag
   this community grew; everything else is a fact about where they sit. */
const TAG_FIELDS = CHIP_FIELDS.filter((field) => field.tags);
const DRAWER_FIELDS = CHIP_FIELDS.filter((field) => !field.tags);

/* The fields "what we have in common" compares. Everything somebody could share with you and
   nothing they could not: a job title you happen to both hold is a coincidence, a hobby is not. */
const COMMON_FIELDS = ['hobbies', 'languages', 'ask_me_about', 'this_or_that', 'based_in'];

/* A chip row past this many is a wall of its own — the rest is one click away. The rows that
   are always on screen get the shorter of the two: four rows of twelve chips over the faces is
   not a wall of people any more, it is a wall of words. */
const CHIPS_SHOWN = 12;
const CHIPS_OPEN = 8;

/* Two answers about time, both sayable without knowing anything else about the company. A chip
   per year would be a decade of chips that each find one person. */
const NEW_YEARS = 2;
const LONG_YEARS = 5;

const Chip = ({ on, onClick, children, n = null }) => (
  <button type="button" className={`cf-chip ${on ? 'is-on' : ''}`} onClick={onClick}
          aria-pressed={on}>
    {children}
    {n !== null && <span className="cw-chip__n">{n}</span>}
  </button>
);

/* One field's chips: the values people on THIS wall actually carry, commonest first, capped until
   somebody asks for the rest. A row with one value is not drawn at all — it filters nothing and
   it costs a line above the faces. */
const ChipRow = ({ label, entries, picked, onToggle, translate, max = CHIPS_SHOWN }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  if (entries.length < 2) return null;
  const shown = all ? entries : entries.slice(0, max);
  return (
    <>
      <span className="cw-rowlabel">{label}</span>
      <div className="cf-filters">
        {shown.map((entry) => (
          <Chip key={entry.key} on={picked.includes(entry.key)} n={entry.n}
                onClick={() => onToggle(entry.key)}>
            {translate ? translate(entry.key, t) : entry.label}
          </Chip>
        ))}
        {entries.length > max && (
          <button type="button" className="cw-more" onClick={() => setAll(!all)}>
            {all ? t('communityFewer') : t('communityMoreValues', { n: entries.length - max })}
          </button>
        )}
      </div>
    </>
  );
};

/* ``facesOnly`` — drop everybody who has no photograph.

   ★ On for the MEMBER's wall (``/photo-wall?source=people``) and off for the admin's Community tab,
   and the two are different jobs rather than two settings. A member is here to look at the people
   around them, and a grid where half the tiles are a grey initial is a directory of absences — it
   makes the community look emptier than it is and gives the reader nothing to recognise anybody by.
   The ADMIN is here to find out who is missing: the "no photo" chip beside "quiet" exists exactly
   to chase them, and filtering those people out would leave that chip pointing at nobody.

   The wall's own rules are unchanged and still come first: a public profile, and — on a business
   unit's wall — the person's own yes. */
const CommunityWall = ({ facesOnly = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [dids, setDids] = useState([]);
  const [tags, setTags] = useState([]);
  const [picked, setPicked] = useState({});   // field key -> chosen values (normalised)
  const [since, setSince] = useState(null);   // null | 'new' | 'long'
  const [common, setCommon] = useState(false);
  const [more, setMore] = useState(false);
  const [only, setOnly] = useState(null);      // admin: 'quiet' | 'nophoto' | null
  const [consentBusy, setConsentBusy] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', '/company/community')
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [t]);

  /* Saying yes or no to being listed. The whole wall is re-read afterwards rather than patched in
     the browser: the answer changes who is ON it, and a screen that added your own tile without
     asking the server would be guessing at a set the server owns. */
  const setVisible = useCallback((visible) => {
    setConsentBusy(true);
    setError(null);
    apiRequest('PUT', '/profile/community-visible', { visible })
      .then(() => load())
      .catch((err) => setError(apiErrorText(t, err)))
      .finally(() => setConsentBusy(false));
  }, [t, load]);

  useEffect(() => { load(); }, [load]);

  /* Typing filters a list already in memory, so the delay is only there to stop the whole wall
     re-laying out on every keystroke. */
  useEffect(() => {
    const id = setTimeout(() => setQuery(term.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [term]);

  /* ★ Applied HERE rather than in the filter below, so it bounds the set everything else is
     computed from: the chip rows, their counts and the "in common" toggle all read `people`, and a
     wall that filtered only its grid would offer a chip that finds four people and then draws
     one. */
  const people = useMemo(() => {
    const all = (data && data.people) || [];
    return facesOnly ? all.filter((p) => (p.photo_url || '').trim()) : all;
  }, [data, facesOnly]);

  /* Read off the UNFILTERED list on purpose: somebody with no photograph is exactly the person
     `people` has just dropped, and they are the one who needs telling. */
  const iHaveAPhoto = useMemo(() => {
    const mine = ((data && data.people) || []).find((p) => p.id === (data && data.me));
    return !!(mine && (mine.photo_url || '').trim());
  }, [data]);

  const counts = useMemo(() => {
    const did = {};
    const tag = {};
    people.forEach((p) => {
      (p.did || []).forEach((k) => { did[k] = (did[k] || 0) + 1; });
      (p.tags || []).forEach((k) => { tag[k] = (tag[k] || 0) + 1; });
    });
    return { did, tag };
  }, [people]);

  /* Every chip row in one pass: for each field, each distinct value with how many people carry it,
     commonest first and then alphabetically so the tail does not reshuffle on every load. */
  const rows = useMemo(() => {
    const out = {};
    CHIP_FIELDS.forEach((field) => {
      const seen = new Map();
      people.forEach((p) => {
        field.values(p).forEach((raw) => {
          const key = norm(raw);
          if (!key) return;
          const entry = seen.get(key) || { key, label: String(raw).trim(), n: 0 };
          entry.n += 1;
          seen.set(key, entry);
        });
      });
      out[field.key] = [...seen.values()]
        .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
    });
    return out;
  }, [people]);

  /* Which behaviour chips exist at all: only the ones somebody on this wall actually carries. A
     chip that can never match is a promise the screen cannot keep. */
  const didKeys = useMemo(
    () => Object.keys(DID_ICONS).filter((k) => counts.did[k]), [counts]);

  const thisYear = new Date().getFullYear();
  const sinceKeys = useMemo(() => {
    const years = people.map((p) => p.since_year).filter(Boolean);
    return [
      years.some((y) => y >= thisYear - NEW_YEARS) && 'new',
      years.some((y) => y <= thisYear - LONG_YEARS) && 'long',
    ].filter(Boolean);
  }, [people, thisYear]);

  /* What the viewer themselves said. Off the payload rather than off their own tile: a person who
     switched their profile off is not on this wall, and they are exactly who is most likely to be
     looking for a way into it. */
  const mine = useMemo(() => {
    const own = (data && data.my_profile) || {};
    const set = new Set();
    COMMON_FIELDS.forEach((key) => {
      const field = CHIP_FIELDS.find((f) => f.key === key);
      (field ? field.values(own) : []).forEach((v) => set.add(`${key}:${norm(v)}`));
    });
    return set;
  }, [data]);

  const sharedWith = useCallback((person) => {
    const out = [];
    COMMON_FIELDS.forEach((key) => {
      const field = CHIP_FIELDS.find((f) => f.key === key);
      (field ? field.values(person) : []).forEach((value) => {
        if (mine.has(`${key}:${norm(value)}`)) out.push({ key, value });
      });
    });
    return out;
  }, [mine]);

  const shown = useMemo(() => {
    const terms = query ? query.split(/\s+/) : [];
    return people.filter((p) => {
      /* The search reads everything on the tile: a name, what they do and where they sit. */
      const haystack = [p.name, p.role, p.department, p.based_in]
        .filter(Boolean).join(' ').toLowerCase();
      if (!terms.every((word) => haystack.includes(word))) return false;
      if (!matchesAny(dids, p.did)) return false;
      if (!matchesAny(tags, p.tags)) return false;
      if (!CHIP_FIELDS.every((field) => matchesAny(picked[field.key] || [],
                                                   field.values(p).map(norm)))) return false;
      if (since === 'new' && !(p.since_year >= thisYear - NEW_YEARS)) return false;
      if (since === 'long' && !(p.since_year && p.since_year <= thisYear - LONG_YEARS)) return false;
      if (common && sharedWith(p).length === 0) return false;
      if (only === 'quiet' && (p.did || []).length > 0) return false;
      if (only === 'nophoto' && p.photo_url) return false;
      return true;
    });
  }, [people, query, dids, tags, picked, since, common, only, sharedWith, thisYear]);

  const toggle = (list, setList) => (value) => setList(
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const togglePicked = (field) => (value) => setPicked((current) => {
    const list = current[field] || [];
    return { ...current,
             [field]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] };
  });

  if (data === null) {
    return error ? <p className="bravos-cal__notice">{error}</p>
      : <span className="auth__spinner" />;
  }

  const profileFilters = Object.values(picked).reduce((n, list) => n + list.length, 0)
    + (since ? 1 : 0);
  /* ★ The badge and the auto-open count only what is INSIDE the drawer. Counting every profile
     filter would put a "2" on a closed drawer that holds neither of them, and open it for a chip
     the reader can already see. */
  const drawerFilters = DRAWER_FIELDS.reduce((n, f) => n + (picked[f.key] || []).length, 0)
    + (since ? 1 : 0);
  const filtering = query || dids.length || tags.length || profileFilters || common || only;
  /* A filter that is on must never be hidden behind a closed drawer — that is a screen showing
     eleven of forty people for no visible reason. */
  const drawerOpen = more || drawerFilters > 0;

  return (
    <div className="cw">
      <p className="bravos-q__hint">{t('communityNote')}</p>

      {/* ★ The ask, and it is on THIS screen rather than in a settings menu because this is the
          one place somebody can see what they would be joining before they answer. Only on the
          business unit's wall (`needs_consent`) — on your own company's roster there is nothing
          to opt into, and a switch offering to hide you from your own colleagues would be a
          different promise from the one the customer bought.

          ★ Both directions are drawn. A consent you cannot withdraw on the same screen you gave
          it is not one, and the "take me off" line is deliberately as easy to find as the yes. */}
      {data.needs_consent && (
        data.my_community_visible ? (
          <>
            <p className="cw-consent cw-consent--on">
              <Icon name="check-circle" className="me-2" />
              {t('communityListedOn')}
              <button type="button" className="cw-consent__off" disabled={consentBusy}
                      onClick={() => setVisible(false)}>{t('communityTakeMeOff')}</button>
            </p>
            {/* ★ Said yes, and still nowhere to be seen. This wall shows faces only, so a person
                without a photograph has just agreed to appear on a screen that cannot draw them —
                a dead end they would otherwise have to work out for themselves. Only on the
                faces-only wall; the admin's tab lists them either way. */}
            {facesOnly && !iHaveAPhoto && (
              <button type="button" className="bravos-submit bravos-submit--ghost cw-prompt"
                      onClick={() => navigate('/edit-profile')}>
                <Icon name="camera" /> {t('communityNeedPhoto')}
              </button>
            )}
          </>
        ) : (
          <div className="cw-consent">
            <p className="cw-consent__title">{t('communityJoinTitle')}</p>
            <p className="cw-consent__body">{t('communityJoinBody')}</p>
            <button type="button" className="bravos-submit" disabled={consentBusy}
                    onClick={() => setVisible(true)}>{t('communityJoinYes')}</button>
          </div>
        )
      )}

      {/* ★ A door, not the game. The guess-who used to open INSIDE this wall, which put a board
          of three faces on top of forty and made one screen answer two errands — "find me a
          colleague" and "test me on them". It has its own page now; this is the line that says so,
          in the one place somebody is already thinking about the people here. */}
      <button type="button" className="gw-open" onClick={() => navigate('/know-your-tribe')}>
        <Icon name="zoom-in" className="me-1" />
        <span className="gw-open__title">{t('quizTitle')}</span>
        <span className="gw-open__cta">{t('quizPlay')}</span>
      </button>

      <input
        type="search"
        className="gl-search cw-search"
        maxLength={80}
        value={term}
        placeholder={t('communitySearch')}
        onChange={(e) => setTerm(e.target.value)}
      />

      {/* What they do. The first row, because it is the question the wall exists to answer. */}
      {didKeys.length > 0 && (
        <>
          <span className="cw-rowlabel">{t('communityWhatTheyDo')}</span>
          <div className="cf-filters">
            {didKeys.map((key) => (
              <Chip key={key} on={dids.includes(key)} n={counts.did[key]}
                    onClick={() => toggle(dids, setDids)(key)}>
                <Icon name={DID_ICONS[key]} className="me-1" />{t(`did_${key}`)}
              </Chip>
            ))}
          </div>
        </>
      )}

      {/* ★ The one button this whole screen is for. Hidden when the viewer has said nothing —
          a button that can only ever find nobody teaches people the wall is broken, and the
          prompt at the bottom is already asking them for the thing that would fix it. */}
      {mine.size > 0 && (
        <div className="cf-filters cw-common">
          <Chip on={common} onClick={() => setCommon(!common)}>
            <Icon name="users-alt" className="me-1" />{t('communityInCommon')}
          </Chip>
        </div>
      )}

      {/* The words people gave themselves. Commonest first, and the count on every chip is the
          answer to "what has this community grown" as much as it is a filter. */}
      {TAG_FIELDS.map((field) => (
        <ChipRow
          key={field.key}
          label={t(field.label)}
          entries={rows[field.key] || []}
          picked={picked[field.key] || []}
          onToggle={togglePicked(field.key)}
          translate={field.translate}
          max={CHIPS_OPEN}
        />
      ))}

      <button type="button" className="cw-drawer" onClick={() => setMore(!more)}
              aria-expanded={drawerOpen}>
        <Icon name={drawerOpen ? 'angle-small-up' : 'angle-small-down'} className="me-1" />
        {t('communityMoreFilters')}
        {drawerFilters > 0 && <span className="cw-chip__n">{drawerFilters}</span>}
      </button>

      {drawerOpen && (
        <div className="cw-drawer__body">
          {DRAWER_FIELDS.map((field) => (
            <ChipRow
              key={field.key}
              label={t(field.label)}
              entries={rows[field.key] || []}
              picked={picked[field.key] || []}
              onToggle={togglePicked(field.key)}
              translate={field.translate}
            />
          ))}

          {sinceKeys.length > 0 && (
            <>
              <span className="cw-rowlabel">{t('pf_since_year')}</span>
              <div className="cf-filters">
                {sinceKeys.map((key) => (
                  <Chip key={key} on={since === key}
                        onClick={() => setSince(since === key ? null : key)}>
                    {t(key === 'new' ? 'communitySinceNew' : 'communitySinceLong')}
                  </Chip>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ★ The company's own words for its people, and the two diagnostics — all three only for
          the person who can act on them. A colleague browsing the community is looking for a
          person, not for how the office has labelled one; and "who is quiet" is the engagement
          score in binary, which is exactly what keeping the number off this wall was for. */}
      {data.is_admin && (
        <>
          {(data.tags || []).length > 0 && (
            <>
              <span className="cw-rowlabel">{t('filterByTag')}</span>
              <div className="cf-filters">
                {data.tags.map((tag) => (
                  <Chip key={tag} on={tags.includes(tag)} n={counts.tag[tag] || 0}
                        onClick={() => toggle(tags, setTags)(tag)}>
                    {tag}
                  </Chip>
                ))}
              </div>
            </>
          )}
          {/* ★ "No photo" is not offered where people without one are not on the wall — a chip
              that can only ever find nobody teaches the reader the filters are broken. It stays
              on the admin's own tab, which is where chasing them belongs. */}
          <div className="cf-filters">
            {[['quiet', 'communityQuiet'], ['nophoto', 'communityNoPhoto']]
              .filter(([key]) => !(facesOnly && key === 'nophoto'))
              .map(([key, label]) => (
                <Chip key={key} on={only === key} onClick={() => setOnly(only === key ? null : key)}>
                  {t(label)}
                </Chip>
              ))}
          </div>
        </>
      )}

      {filtering ? (
        <p className="ce-note cw-count">
          {t('communityShowing', { n: shown.length, total: people.length })}
        </p>
      ) : null}

      {people.length === 0 && <p className="ce-note">{t('communityNobody')}</p>}
      {people.length > 0 && shown.length === 0 && (
        <p className="ce-note">{t('communityNoMatch')}</p>
      )}

      <div className="cw-grid">
        {shown.map((p) => {
          const me = p.id === data.me;
          const shares = common ? sharedWith(p) : [];
          return (
            <div key={p.id} className={`cw-tile ${me ? 'is-me' : ''}`}>
              <button
                type="button"
                className="cw-tile__open"
                onClick={() => navigate(`/u/${p.id}`)}
              >
                {p.photo_url
                  ? <img className="cw-tile__img" src={p.photo_url} alt="" loading="lazy" />
                  : (
                    <span className="cw-tile__img cw-tile__img--empty">
                      {(p.name || '?').trim().charAt(0) || '?'}
                    </span>
                  )}
                <span className="cw-tile__name">{p.name}</span>
                {/* What they do here, under the name — the first thing anybody looking for a
                    colleague actually needs, and the reason the search reads it too. */}
                {(p.role || p.department) && (
                  <span className="cw-tile__role">
                    {[p.role, p.department].filter(Boolean).join(' · ')}
                  </span>
                )}
                {shares.length > 0 && (
                  <span className="cw-tile__shared">
                    {shares.slice(0, 2).map((s) => (s.key === 'this_or_that'
                      ? t(`pfTotv_${s.value.split(':')[1]}`) : s.value)).join(' · ')}
                  </span>
                )}
              </button>

              {(p.did || []).length > 0 && (
                <span className="cw-tile__did">
                  {/* Two at most: a strip of six icons over a face is decoration, and the list
                      they come from arrives strongest first. "Comes along" is dropped beside
                      "hosts" — the server says both so the FILTER can find a host, but on a tile
                      with room for two the weaker of a pair that implies each other would cost
                      the reader the one thing they did not already know. */}
                  {p.did.filter((k) => !(k === 'showing_up' && p.did.includes('hosted')))
                    .slice(0, 2).map((key) => (
                    <span key={key} title={t(`did_${key}`)}>
                      <Icon name={DID_ICONS[key]} />
                    </span>
                  ))}
                </span>
              )}
              {me && <span className="cw-tile__me">{t('communityYou')}</span>}
              {/* ★ Cheer this colleague (F7), straight off their face. The wall is where somebody
                  is already looking at the people they work with, so it is where remembering what
                  one of them did actually happens — a composer you have to go and find gets used
                  the week it ships and never again. Never on your own tile. */}
              {!me && (
                <button type="button" className="cw-tile__cheer" title={t('cheerTitle')}
                        onClick={(e) => { e.stopPropagation(); navigate(`/cheer/${p.id}`); }}>
                  <Icon name="hands-clapping" />
                </button>
              )}
              {/* ★ Outside the tile's own button, not inside it: a link within a button is
                  invalid markup and the click would open the profile as well.
                  ★ TWO glyphs at most, and this is arithmetic rather than taste: the grid column
                  is `minmax(92px, 1fr)` and a dot is 22px, so four of them (97px) overflow the
                  narrowest tile on a phone. LinkedIn and WhatsApp are the two people actually
                  reach each other on; the profile behind the tile carries the whole set. */}
              {reachOf(p).map((link) => (
                <a
                  key={link.key}
                  className="cw-tile__li"
                  style={{ left: link.offset }}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={link.title}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon name={link.icon} pack="brands" />
                </a>
              ))}
            </div>
          );
        })}
      </div>

      {/* ★ What stands in for the nudge mail this feature deliberately does not send: the ask is
          on the wall, where the person is already looking at everybody else's. */}
      {data.my_profile_filled === 0 && (
        <button type="button" className="bravos-submit bravos-submit--ghost cw-prompt"
                onClick={() => navigate('/edit-profile')}>
          <Icon name="user" /> {t('communitySaySomething')}
        </button>
      )}

      {data.private_count > 0 && (
        <p className="ce-note">{t('communityPrivate', { n: data.private_count })}</p>
      )}
    </div>
  );
};

export default CommunityWall;
